import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, Send, Sparkles, Download, Eye, X, Upload, Clock, CheckCircle2, Inbox, ArrowLeftRight, Trash2,
} from "lucide-react";
import {
  apiGet, apiPost, apiDelete, handleApiError, KpiCard, Btn, StatoBadge,
  STATO_RICHIESTA_LABELS, formatDate, RICETTE_BASE, API_URL, authHeaders, apiDownload,
  FilterBar, FilterSelect, DetailPanel, OspiteAvatar, ScadenzaCell,
} from "./shared";
import axios from "axios";
import DoctorSelect from "../../../components/doctors/DoctorSelect";
import { useAuth } from "../../../context/AuthContext";

export default function RichiesteFarmacia({ token }) {
  const { activeStruttura } = useAuth();
  const [items, setItems] = useState([]);
  const [kpi, setKpi] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [ospiti, setOspiti] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ fascicolo_id: "", farmaci_richiesti: "", note: "" });
  const [createFile, setCreateFile] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStato, setFilterStato] = useState("");
  const [filterFarmacia, setFilterFarmacia] = useState("");
  const [filterPeriodo, setFilterPeriodo] = useState("30");
  const [actionLoading, setActionLoading] = useState(false);
  const [showInviaMedicoDialog, setShowInviaMedicoDialog] = useState(false);
  const [inviaGroups, setInviaGroups] = useState([]);
  const [inviaMessaggio, setInviaMessaggio] = useState("");
  const [inviaDataInvio, setInviaDataInvio] = useState("");
  const [cronologia, setCronologia] = useState([]);
  const [showCronologia, setShowCronologia] = useState(false);
  const [showAllItems, setShowAllItems] = useState(false);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const fileRef = useRef(null);
  const LIST_PAGE_SIZE = 6;

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [listRes, kpiRes, ospitiRes] = await Promise.all([
        apiGet("/richieste-farmacia", token),
        apiGet("/richieste-farmacia/kpi", token),
        apiGet("/ospiti", token),
      ]);
      setItems(listRes.items || []);
      setKpi(kpiRes);
      setOspiti(ospitiRes.items || []);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoading(false);
    }
  }, [token, activeStruttura?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setShowAllItems(false);
    setCheckedIds(new Set());
  }, [search, filterStato, filterFarmacia, filterPeriodo]);

  const openDetail = async (id) => {
    try {
      const detail = await apiGet(`/richieste-farmacia/${id}`, token);
      setSelected(detail);
      setShowCronologia(false);
      setCronologia([]);
    } catch (err) {
      handleApiError(err);
    }
  };

  const loadCronologia = async (id) => {
    try {
      const res = await apiGet(`/richieste-farmacia/${id}/eventi`, token);
      setCronologia(res.items || []);
      setShowCronologia(true);
    } catch {
      toast.error("Impossibile caricare la cronologia");
    }
  };

  const resetCreateForm = () => {
    setShowCreate(false);
    setForm({ fascicolo_id: "", farmaci_richiesti: "", note: "" });
    setCreateFile(null);
  };

  const handleCreate = async () => {
    if (!form.fascicolo_id) {
      toast.error("Seleziona un ospite");
      return;
    }
    setActionLoading(true);
    try {
      const created = await apiPost("/richieste-farmacia", token, {
        fascicolo_id: form.fascicolo_id,
        farmaci_richiesti: form.farmaci_richiesti.split("\n").filter(Boolean),
        note: form.note,
      });

      if (createFile && created?.id) {
        const fd = new FormData();
        fd.append("file", createFile);
        const res = await axios.post(
          `${RICETTE_BASE}/richieste-farmacia/${created.id}/upload`,
          fd,
          { headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" } },
        );
        const data = res.data || {};
        const extra = data.richieste_aggiuntive || [];
        const nonId = data.pagine_non_identificate || 0;
        if (extra.length > 0) {
          const nomi = extra.map((r) => r.ospite || "ospite sconosciuto").join(", ");
          toast.success(
            `Richiesta creata e allegato suddiviso: +${extra.length} richieste per ${nomi}.`,
            { duration: 6000 },
          );
        } else {
          toast.success("Richiesta creata con allegato");
        }
        if (nonId > 0) {
          toast.warning(
            `${nonId} pagina${nonId > 1 ? "e" : ""} non riconosciuta${nonId > 1 ? "e" : ""}: inviate in Comunicazioni per assegnazione manuale.`,
            { duration: 7000 },
          );
        }
      } else {
        toast.success("Richiesta creata");
      }

      resetCreateForm();
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const medicoGroupKey = (doctorId, email) => {
    if (doctorId) return `doc:${doctorId}`;
    const em = (email || "").trim().toLowerCase();
    if (em && em.includes("@")) return `email:${em}`;
    return null;
  };

  const buildInviaGroupsFromRows = (rows) => {
    const map = new Map();
    for (const row of rows) {
      let key = medicoGroupKey(row.doctorId, row.email) || `req:${row.richiestaId}`;
      if (!map.has(key)) {
        map.set(key, {
          groupKey: key,
          doctorId: row.doctorId,
          doctorName: row.doctorName,
          email: row.email,
          richiestaIds: [],
        });
      }
      map.get(key).richiestaIds.push(row.richiestaId);
    }
    return [...map.values()];
  };

  const regroupInviaGroups = (groups) => {
    const rows = groups.flatMap((group) => group.richiestaIds.map((richiestaId) => ({
      richiestaId,
      doctorId: group.doctorId,
      doctorName: group.doctorName,
      email: group.email,
    })));
    return buildInviaGroupsFromRows(rows);
  };

  const buildInviaRow = async (richiestaId) => {
    const item = items.find((r) => r.id === richiestaId);
    if (!item) return null;
    const ospite = ospiti.find((o) => o.fascicolo_id === item.fascicolo_id);
    const doctorId = ospite?.doctor_id || "";
    const doctorName = ospite?.medico || "";
    let email = "";
    if (doctorId && token) {
      try {
        const res = await axios.get(`${API_URL}/api/doctors/${doctorId}`, { headers: authHeaders(token) });
        email = res.data?.blistering_email || res.data?.email || "";
      } catch {
        // Email compilabile manualmente nel dialog.
      }
    }
    return { richiestaId, doctorId, doctorName, email };
  };

  const openInviaMedicoDialog = async (targetIds) => {
    const ids = (targetIds || []).filter(Boolean);
    if (ids.length === 0) return;

    const rows = (await Promise.all(ids.map((id) => buildInviaRow(id)))).filter(Boolean);
    if (rows.length === 0) return;

    setInviaGroups(buildInviaGroupsFromRows(rows));
    setInviaMessaggio("Richiesta ricette per deblistering.");
    setInviaDataInvio("");
    setShowInviaMedicoDialog(true);
  };

  const updateInviaGroup = (groupKey, patch) => {
    setInviaGroups((prev) => regroupInviaGroups(
      prev.map((group) => (group.groupKey === groupKey ? { ...group, ...patch } : group)),
    ));
  };

  const totalInviaRichieste = inviaGroups.reduce((n, g) => n + g.richiestaIds.length, 0);
  const isInvioProgrammato = Boolean(inviaDataInvio && inviaDataInvio > new Date().toISOString().slice(0, 10));

  const handleInviaMedico = async () => {
    if (inviaGroups.length === 0) return;
    const invalid = inviaGroups.filter((group) => !group.doctorId && (!group.email?.trim() || !group.email.includes("@")));
    if (invalid.length > 0) {
      toast.error(`Completa medico o email per ${invalid.length} grupp${invalid.length === 1 ? "o" : "i"}`);
      return;
    }
    setActionLoading(true);
    const sentIds = inviaGroups.flatMap((group) => group.richiestaIds);
    const sharedMessaggio = inviaMessaggio.trim() || undefined;
    const dataInvioPayload = inviaDataInvio || undefined;
    try {
      if (totalInviaRichieste === 1) {
        const group = inviaGroups[0];
        const res = await apiPost(`/richieste-farmacia/${group.richiestaIds[0]}/invia-medico`, token, {
          doctor_id: group.doctorId || undefined,
          email: group.email.trim() || undefined,
          messaggio: sharedMessaggio,
          data_invio_medico: dataInvioPayload,
        });
        toast.success(res.programmato ? (res.message || "Invio programmato") : "Inviato al medico");
      } else {
        const res = await apiPost("/richieste-farmacia/invia-medico-bulk", token, {
          messaggio: sharedMessaggio,
          data_invio_medico: dataInvioPayload,
          groups: inviaGroups.map((group) => ({
            richiesta_ids: group.richiestaIds,
            doctor_id: group.doctorId || undefined,
            email: group.email.trim() || undefined,
          })),
        });
        if (res.programmato) {
          toast.success(res.message || `${res.inviati} richieste programmate`);
        } else if (res.errori?.length) {
          toast.warning(`Inviate ${res.inviati}/${res.totale} richieste in ${res.email_inviati || 0} email`);
        } else if ((res.email_inviati || 0) < totalInviaRichieste) {
          toast.success(`${res.inviati} richieste inviate in ${res.email_inviati} email ai medici`);
        } else {
          toast.success(`${res.inviati} richieste inviate ai rispettivi medici`);
        }
      }
      setShowInviaMedicoDialog(false);
      setCheckedIds(new Set());
      setInviaGroups([]);
      setInviaDataInvio("");
      if (selected && sentIds.includes(selected.id)) {
        openDetail(selected.id);
      }
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleChecked = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    const allSelected = filteredItems.length > 0 && filteredItems.every((r) => checkedIds.has(r.id));
    if (allSelected) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(filteredItems.map((r) => r.id)));
    }
  };

  const handleAnnullaInvioProgrammato = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await apiPost(`/richieste-farmacia/${selected.id}/annulla-invio-programmato`, token, {});
      toast.success("Invio programmato annullato");
      openDetail(selected.id);
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSpostaARicetta = async () => {
    if (!selected) return;
    if (!window.confirm("Spostare questa richiesta tra le Ricette da medico? Gli allegati vengono preservati.")) return;
    setActionLoading(true);
    try {
      await apiPost(`/richieste-farmacia/${selected.id}/sposta-a-ricetta`, token, {});
      toast.success("Spostata tra le ricette da medico");
      setSelected(null);
      load();
    } catch (err) {
      handleApiError(err, "Errore durante lo spostamento");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteRichieste = async (ids) => {
    const uniqueIds = [...new Set(ids.filter(Boolean))];
    if (uniqueIds.length === 0) return;

    const sentCount = uniqueIds.filter((id) => {
      const item = items.find((r) => r.id === id) || (selected?.id === id ? selected : null);
      return item && ["inviata_medico", "ricette_ricevute", "chiusa", "invio_programmato"].includes(item.stato);
    }).length;

    const label = uniqueIds.length === 1 ? "questa richiesta" : `${uniqueIds.length} richieste`;
    let confirmMsg = `Eliminare ${label}? L'operazione non è reversibile.`;
    if (sentCount > 0) {
      confirmMsg += sentCount === 1
        ? " Una richiesta risulta già inviata (o programmata) al medico."
        : ` ${sentCount} richieste risultano già inviate (o programmate) al medico.`;
    }
    if (!window.confirm(confirmMsg)) return;

    setActionLoading(true);
    try {
      const results = await Promise.allSettled(uniqueIds.map((id) => apiDelete(`/richieste-farmacia/${id}`, token)));
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;

      if (ok === 0) {
        handleApiError(results.find((r) => r.status === "rejected")?.reason, "Errore eliminazione");
        return;
      }
      if (fail > 0) {
        toast.warning(`${ok} eliminate, ${fail} non riuscite`);
      } else {
        toast.success(ok === 1 ? "Richiesta eliminata" : `${ok} richieste eliminate`);
      }

      setCheckedIds((prev) => {
        const next = new Set(prev);
        uniqueIds.forEach((id) => next.delete(id));
        return next;
      });
      if (selected && uniqueIds.includes(selected.id)) {
        setSelected(null);
      }
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    deleteRichieste([selected.id]);
  };

  const handleDeleteSelected = () => {
    deleteRichieste([...checkedIds]);
  };

  const handleAnalizzaIA = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      await apiPost(`/richieste-farmacia/${selected.id}/analizza-ia`, token, {});
      toast.success("Analisi completata");
      openDetail(selected.id);
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpload = async (e) => {
    if (!selected || !e.target.files?.[0]) return;
    const fd = new FormData();
    fd.append("file", e.target.files[0]);
    setActionLoading(true);
    try {
      const res = await axios.post(
        `${RICETTE_BASE}/richieste-farmacia/${selected.id}/upload`,
        fd,
        { headers: { ...authHeaders(token), "Content-Type": "multipart/form-data" } },
      );
      const data = res.data || {};
      const extra = data.richieste_aggiuntive || [];
      const nonId = data.pagine_non_identificate || 0;

      if (extra.length > 0) {
        const nomi = extra.map((r) => r.ospite || "ospite sconosciuto").join(", ");
        toast.success(
          `Allegato caricato e suddiviso: create ${extra.length} richieste aggiuntive per ${nomi}.`,
          { duration: 6000 }
        );
      } else {
        toast.success("Allegato caricato");
      }
      if (nonId > 0) {
        toast.warning(
          `${nonId} pagina${nonId > 1 ? "e" : ""} non riconosciuta${nonId > 1 ? "e" : ""}: inviate in Comunicazioni per assegnazione manuale.`,
          { duration: 7000 }
        );
      }
      openDetail(selected.id);
      load();
    } catch (err) {
      handleApiError(err);
    } finally {
      setActionLoading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleDownload = async (fileId, fileName) => {
    try {
      await apiDownload(`/richieste-farmacia/${selected.id}/allegato/${fileId}/download`, token, fileName);
    } catch (err) {
      handleApiError(err);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-violet-600" /></div>;
  }

  const filteredItems = items.filter((r) => {
    const haystack = `${r.farmacia || ""} ${r.ospite_cognome || ""} ${r.ospite_nome || ""} ${(r.farmaci_richiesti || []).join(" ")}`.toLowerCase();
    if (search && !haystack.includes(search.toLowerCase())) return false;
    if (filterStato && r.stato !== filterStato) return false;
    if (filterFarmacia && !(r.farmacia || "").toLowerCase().includes(filterFarmacia.toLowerCase())) return false;
    if (filterPeriodo && filterPeriodo !== "tutti") {
      const giorni = parseInt(filterPeriodo, 10);
      const cutoff = new Date(Date.now() - giorni * 86400000);
      const dt = r.data_richiesta || r.created_at;
      if (dt && new Date(dt) < cutoff) return false;
    }
    return true;
  });
  const farmacie = [...new Set(items.map(i => i.farmacia).filter(Boolean))];
  const visibleItems = showAllItems ? filteredItems : filteredItems.slice(0, LIST_PAGE_SIZE);
  const hiddenCount = Math.max(filteredItems.length - LIST_PAGE_SIZE, 0);
  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every((r) => checkedIds.has(r.id));
  const someFilteredSelected = filteredItems.some((r) => checkedIds.has(r.id));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard icon={Inbox} label="Totali richieste" value={kpi.totale} subtitle="Ultimi 30 giorni" tone="blue" />
        <KpiCard icon={Clock} label="Da gestire" value={kpi.da_gestire} subtitle={`${kpi.da_gestire || 0} in scadenza`} tone="amber" />
        <KpiCard icon={Clock} label="In attesa medico" value={kpi.in_attesa_medico} subtitle="Ricette mancanti" tone="violet" />
        <KpiCard icon={Send} label="Inviate al medico" value={kpi.inviate_medico} subtitle="In attesa risposta" tone="emerald" />
        <KpiCard icon={CheckCircle2} label="Chiuse" value={kpi.chiuse} subtitle="Questo mese" tone="blue" />
      </div>

      <FilterBar
        filters={(
          <>
            <FilterSelect onChange={e => setFilterFarmacia(e.target.value)} value={filterFarmacia}>
              <option value="">Tutte le farmacie</option>
              {farmacie.map(f => <option key={f} value={f}>{f}</option>)}
            </FilterSelect>
            <FilterSelect onChange={e => setFilterStato(e.target.value)} value={filterStato}>
              <option value="">Tutti gli stati</option>
              <option value="da_gestire">Da gestire</option>
              <option value="in_attesa_medico">In attesa medico</option>
              <option value="invio_programmato">Invio programmato</option>
              <option value="inviata_medico">Inviata al medico</option>
              <option value="ricette_ricevute">Ricette ricevute</option>
              <option value="chiusa">Chiusa</option>
            </FilterSelect>
            <FilterSelect onChange={e => setFilterPeriodo(e.target.value)} value={filterPeriodo}>
              <option value="7">Ultimi 7 giorni</option>
              <option value="30">Ultimi 30 giorni</option>
              <option value="90">Ultimi 90 giorni</option>
              <option value="tutti">Tutti</option>
            </FilterSelect>
          </>
        )}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Cerca richiesta..."
        action={<Btn onClick={() => setShowCreate(true)}><Plus className="w-4 h-4" /> Nuova richiesta</Btn>}
      />

      {checkedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-violet-200 bg-violet-50/60 px-4 py-3">
          <span className="text-sm font-medium text-violet-900">
            {checkedIds.size} richiest{checkedIds.size === 1 ? "a selezionata" : "e selezionate"}
          </span>
          <Btn variant="secondary" className="text-sm" onClick={() => openInviaMedicoDialog([...checkedIds])} disabled={actionLoading}>
            <Send className="w-4 h-4" /> Invia al medico
          </Btn>
          <Btn variant="danger" className="text-sm" onClick={handleDeleteSelected} disabled={actionLoading}>
            <Trash2 className="w-4 h-4" /> Elimina
          </Btn>
          <button type="button" className="text-sm text-violet-700 hover:underline" onClick={() => setCheckedIds(new Set())}>
            Deseleziona tutte
          </button>
        </div>
      )}

      <div className="grid xl:grid-cols-12 gap-4">
        <div className={`${selected ? "xl:col-span-9" : "xl:col-span-12"}`}>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    <th className="p-3 w-10">
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                        checked={allFilteredSelected}
                        ref={(el) => {
                          if (el) el.indeterminate = someFilteredSelected && !allFilteredSelected;
                        }}
                        onChange={toggleSelectAllFiltered}
                        title="Seleziona tutte le richieste filtrate"
                        aria-label="Seleziona tutte"
                      />
                    </th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Data richiesta</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Farmacia</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Ospite</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs hidden lg:table-cell">Farmaci richiesti</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Stato</th>
                    <th className="text-left p-3 font-semibold text-slate-500 text-xs">Scadenza</th>
                    <th className="p-3 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.length === 0 ? (
                    <tr><td colSpan={8} className="p-8 text-center text-slate-500">Nessuna richiesta</td></tr>
                  ) : visibleItems.map((r) => (
                    <tr
                      key={r.id}
                      className={`border-b border-slate-50 hover:bg-violet-50/40 cursor-pointer transition ${selected?.id === r.id ? "bg-violet-50/60" : ""} ${checkedIds.has(r.id) ? "bg-violet-50/30" : ""}`}
                      onClick={() => openDetail(r.id)}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                          checked={checkedIds.has(r.id)}
                          onChange={() => toggleChecked(r.id)}
                          aria-label={`Seleziona richiesta ${r.ospite_cognome} ${r.ospite_nome}`}
                        />
                      </td>
                      <td className="p-3 text-slate-600 whitespace-nowrap">{formatDate(r.data_richiesta)}</td>
                      <td className="p-3">
                        <div className="font-medium text-slate-800">{r.farmacia || "—"}</div>
                        {r.email_farmacia && <div className="text-xs text-slate-400 truncate">{r.email_farmacia}</div>}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <OspiteAvatar cognome={r.ospite_cognome} nome={r.ospite_nome} className="w-7 h-7 text-[10px]" />
                          <div className="min-w-0">
                            <div className="truncate">{r.ospite_cognome} {r.ospite_nome}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-3 hidden lg:table-cell">
                        <div className="text-xs text-slate-600">{(r.farmaci_richiesti || []).length} farmaci</div>
                        <div className="text-xs text-slate-400 truncate">{(r.farmaci_richiesti || []).slice(0, 2).join(", ")}</div>
                      </td>
                      <td className="p-3">
                        <div className="flex flex-col gap-1">
                          <StatoBadge stato={r.stato} map={STATO_RICHIESTA_LABELS} />
                          {r.origine === "portale_farmacia" && (
                            <span className="text-[10px] font-medium text-emerald-700">Portale farmacia</span>
                          )}
                          {(r.solleciti_count || 0) > 0 && (
                            <span className="text-[10px] text-amber-700">{r.solleciti_count} solleciti</span>
                          )}
                        </div>
                      </td>
                      <td className="p-3">
                        {r.stato === "invio_programmato" && r.invio_medico_programmato_at ? (
                          <div>
                            <div className="text-xs text-sky-700 font-medium">Invio {formatDate(r.invio_medico_programmato_at)}</div>
                            <ScadenzaCell date={r.scadenza} />
                          </div>
                        ) : (
                          <ScadenzaCell date={r.scadenza} />
                        )}
                      </td>
                      <td className="p-3">
                        <button type="button" className="p-1.5 text-slate-400 hover:text-violet-600 rounded-lg hover:bg-violet-50" onClick={(e) => { e.stopPropagation(); openDetail(r.id); }}>
                          <Eye className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredItems.length > LIST_PAGE_SIZE && (
              <div className="text-center py-3 border-t border-slate-100">
                <button
                  type="button"
                  className="text-xs text-violet-600 hover:underline"
                  onClick={() => setShowAllItems((prev) => !prev)}
                >
                  {showAllItems
                    ? "Mostra meno"
                    : `Mostra altri ${hiddenCount} risultat${hiddenCount === 1 ? "o" : "i"}`}
                </button>
              </div>
            )}
          </div>
        </div>

        {selected && (
          <DetailPanel
            title="Dettaglio richiesta"
            icon={Inbox}
            onClose={() => setSelected(null)}
            className="xl:col-span-3 max-h-[640px]"
          >
            <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 space-y-1">
              <div className="font-semibold text-slate-900">{selected.farmacia || "Farmacia"}</div>
              <div className="text-xs text-slate-500">{selected.email_farmacia || "—"}</div>
              <div className="pt-1 flex flex-wrap items-center gap-1.5">
                <StatoBadge stato={selected.stato} map={STATO_RICHIESTA_LABELS} />
                {selected.origine === "portale_farmacia" && (
                  <span className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-medium">
                    Portale farmacia
                  </span>
                )}
                {selected.invio_medico_at && selected.origine === "portale_farmacia" && (
                  <span className="rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 text-[10px] font-medium">
                    Inoltrata al medico dalla farmacia
                  </span>
                )}
                {(selected.solleciti_count || 0) > 0 && (
                  <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-medium">
                    {selected.solleciti_count} solleciti
                  </span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-slate-400 block">Data richiesta</span><span className="font-medium">{formatDate(selected.data_richiesta)}</span></div>
              <div>
                <span className="text-slate-400 block">{selected.stato === "invio_programmato" ? "Invio programmato" : "Scadenza"}</span>
                {selected.stato === "invio_programmato" && selected.invio_medico_programmato_at ? (
                  <span className="font-medium text-sky-700">{formatDate(selected.invio_medico_programmato_at)}</span>
                ) : (
                  <ScadenzaCell date={selected.scadenza} />
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <OspiteAvatar cognome={selected.ospite_cognome} nome={selected.ospite_nome} />
              <div>
                <div className="font-semibold">{selected.ospite_cognome} {selected.ospite_nome}</div>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 mb-2">Farmaci richiesti ({(selected.farmaci_richiesti || []).length})</div>
              <ul className="space-y-1.5">
                {(selected.farmaci_richiesti || []).map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                    <span className="text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
            </div>
            {(selected.allegati || []).length > 0 && (
              <div>
                <div className="text-xs font-semibold text-slate-500 mb-2">Allegati ricevuti</div>
                <ul className="space-y-1">
                  {selected.allegati.map((a) => (
                    <li key={a.id} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-2 py-1.5 border border-slate-100">
                      <span className="flex-1 truncate">{a.file_name}</span>
                      {a.pagine && (
                        <span className="shrink-0 rounded bg-violet-100 text-violet-700 px-1.5 py-0.5 text-[10px] font-medium">
                          pp. {a.pagine[0]}–{a.pagine[1]}
                        </span>
                      )}
                      {selected.origine === "split_pdf" && (
                        <span className="shrink-0 rounded bg-amber-100 text-amber-700 px-1.5 py-0.5 text-[10px] font-medium">
                          scorporato
                        </span>
                      )}
                      <button type="button" className="text-violet-600 hover:text-violet-800" onClick={() => handleDownload(a.id, a.file_name)}>
                        <Download className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => showCronologia ? setShowCronologia(false) : loadCronologia(selected.id)}
                className="text-xs text-violet-600 hover:underline flex items-center gap-1"
              >
                <Clock className="w-3 h-3" />
                {showCronologia ? "Nascondi cronologia" : "Mostra cronologia pratica"}
              </button>
              {showCronologia && (
                <div className="mt-2 space-y-1.5">
                  {cronologia.length === 0 ? (
                    <p className="text-xs text-slate-400 italic">Nessun evento registrato</p>
                  ) : cronologia.map((ev) => (
                    <div key={ev.id} className="flex items-start gap-2 text-xs">
                      <span className="w-1.5 h-1.5 rounded-full bg-violet-400 mt-1.5 shrink-0" />
                      <div>
                        <span className="font-medium text-slate-700">{ev.descrizione || ev.tipo}</span>
                        <span className="text-slate-400 ml-1.5">{formatDate(ev.created_at)}</span>
                        {ev.user_nome && <span className="text-slate-400 ml-1">— {ev.user_nome}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
              {selected.stato === "invio_programmato" ? (
                <Btn variant="secondary" className="w-full" onClick={handleAnnullaInvioProgrammato} disabled={actionLoading}>
                  <X className="w-4 h-4" /> Annulla invio programmato
                </Btn>
              ) : (
                <Btn variant="secondary" className="w-full" onClick={() => openInviaMedicoDialog([selected.id])} disabled={actionLoading}><Send className="w-4 h-4" /> Invia al medico</Btn>
              )}
              {!["inviata_medico", "ricette_ricevute", "chiusa"].includes(selected.stato) && (
                <Btn
                  variant="soft"
                  className="w-full text-xs"
                  disabled={actionLoading}
                  onClick={async () => {
                    setActionLoading(true);
                    try {
                      await apiPost(`/richieste-farmacia/${selected.id}/segna-invio-medico`, token, { canale: "whatsapp" });
                      toast.success("Segnato invio al medico (WhatsApp / manuale)");
                      await load();
                      const refreshed = (await apiGet("/richieste-farmacia", token)).items?.find((r) => r.id === selected.id);
                      if (refreshed) setSelected(refreshed);
                    } catch (err) {
                      handleApiError(err, "Errore");
                    } finally {
                      setActionLoading(false);
                    }
                  }}
                >
                  Segna inviato (WhatsApp / manuale)
                </Btn>
              )}
              <div className="flex gap-2">
                <Btn variant="secondary" className="flex-1" onClick={() => fileRef.current?.click()} disabled={actionLoading}>
                  <Upload className="w-4 h-4" /> Allegato
                </Btn>
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={handleUpload} />
                <Btn variant="soft" className="flex-1" onClick={handleAnalizzaIA} disabled={actionLoading}>
                  <Sparkles className="w-4 h-4" /> Analizza
                </Btn>
              </div>
              <Btn variant="secondary" className="w-full text-xs" onClick={handleSpostaARicetta} disabled={actionLoading}>
                <ArrowLeftRight className="w-3.5 h-3.5" /> Sposta → Ricette medico
              </Btn>
              <Btn variant="danger" className="w-full text-xs" onClick={handleDelete} disabled={actionLoading}>
                <Trash2 className="w-3.5 h-3.5" /> Elimina richiesta
              </Btn>
            </div>
          </DetailPanel>
        )}
      </div>

      {showInviaMedicoDialog && inviaGroups.length > 0 && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-2xl bg-white rounded-xl border border-slate-200 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">
                  {totalInviaRichieste > 1
                    ? `Invia ${totalInviaRichieste} richieste (${inviaGroups.length} email)`
                    : "Invia richiesta al medico"}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {totalInviaRichieste > 1
                    ? "Le richieste con lo stesso medico sono raggruppate in un'unica email. Verifica i destinatari."
                    : "Verifica i dati e invia email con farmaci e allegati al medico."}
                </p>
              </div>
              <button type="button" onClick={() => { setShowInviaMedicoDialog(false); setInviaGroups([]); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                {inviaGroups.map((group) => (
                  <div key={group.groupKey} className="rounded-lg border border-slate-200 p-3 space-y-3 bg-slate-50/50">
                    {group.richiestaIds.length > 1 && (
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-700">
                        {group.richiestaIds.length} richieste — 1 email al medico
                      </div>
                    )}
                    <div className="space-y-2">
                      {group.richiestaIds.map((richiestaId) => {
                        const item = items.find((r) => r.id === richiestaId);
                        if (!item) return null;
                        return (
                          <div key={richiestaId} className="flex items-start gap-2 pb-2 border-b border-slate-100 last:border-0 last:pb-0">
                            <OspiteAvatar cognome={item.ospite_cognome} nome={item.ospite_nome} className="w-8 h-8 text-xs shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold text-slate-900">{item.ospite_cognome} {item.ospite_nome}</div>
                              <div className="text-xs text-slate-500">{item.farmacia || "Farmacia"} · {formatDate(item.data_richiesta)}</div>
                              <div className="text-xs text-slate-600 mt-0.5">
                                {(item.farmaci_richiesti || []).length} farmaci
                                {(item.allegati || []).length > 0 && ` · ${item.allegati.length} allegat${item.allegati.length === 1 ? "o" : "i"}`}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-700">Medico destinatario</label>
                      <DoctorSelect
                        token={token}
                        value={group.doctorId}
                        displayName={group.doctorName}
                        onSelect={(doctor) => updateInviaGroup(group.groupKey, {
                          doctorId: doctor.id,
                          doctorName: doctor.name,
                          email: doctor.blistering_email || doctor.email || "",
                        })}
                        onClear={() => updateInviaGroup(group.groupKey, { doctorId: "", doctorName: "", email: "" })}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-700">Email medico</label>
                      <input
                        type="email"
                        className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                        placeholder="medico@esempio.it"
                        value={group.email}
                        onChange={(e) => updateInviaGroup(group.groupKey, { email: e.target.value })}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Data invio ai medici</label>
                <input
                  type="date"
                  className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30"
                  value={inviaDataInvio}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setInviaDataInvio(e.target.value)}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Lascia vuoto per inviare subito. Se scegli una data futura, l&apos;email partirà automaticamente alle 08:00 di quel giorno.
                </p>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700">Messaggio email (comune)</label>
                <textarea
                  className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-none"
                  rows={3}
                  value={inviaMessaggio}
                  onChange={(e) => setInviaMessaggio(e.target.value)}
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Btn variant="secondary" onClick={() => { setShowInviaMedicoDialog(false); setInviaGroups([]); }} disabled={actionLoading}>
                  Annulla
                </Btn>
                <Btn onClick={handleInviaMedico} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {actionLoading
                    ? (isInvioProgrammato ? "Programmazione..." : "Invio in corso...")
                    : isInvioProgrammato
                      ? (totalInviaRichieste > 1
                        ? `Programma ${inviaGroups.length} email (${totalInviaRichieste} richieste)`
                        : "Programma invio")
                      : (totalInviaRichieste > 1
                        ? `Invia ${inviaGroups.length} email (${totalInviaRichieste} richieste)`
                        : "Invia al medico")}
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="w-full max-w-md bg-white rounded-xl border border-slate-200 shadow-xl">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">Nuova richiesta farmacia</h3>
              <button type="button" onClick={resetCreateForm} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Ospite</label>
                <select
                  className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  value={form.fascicolo_id}
                  onChange={(e) => setForm({ ...form, fascicolo_id: e.target.value })}
                >
                  <option value="">Seleziona...</option>
                  {ospiti.map((o) => (
                    <option key={o.fascicolo_id} value={o.fascicolo_id}>
                      {o.cognome} {o.nome}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Farmaci (uno per riga)</label>
                <textarea
                  className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  rows={4}
                  value={form.farmaci_richiesti}
                  onChange={(e) => setForm({ ...form, farmaci_richiesti: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Note</label>
                <textarea
                  className="w-full mt-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                  rows={2}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Allegato (opzionale)</label>
                <div className="mt-1 flex items-center gap-2">
                  <label className="flex-1 flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:bg-slate-50 cursor-pointer transition">
                    <Upload className="w-4 h-4 shrink-0 text-slate-400" />
                    <span className="truncate">
                      {createFile ? createFile.name : "PDF o immagine — puoi aggiungerlo anche dopo"}
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => setCreateFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  {createFile && (
                    <button
                      type="button"
                      onClick={() => setCreateFile(null)}
                      className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-50"
                      title="Rimuovi allegato"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Btn variant="secondary" onClick={resetCreateForm}>Annulla</Btn>
                <Btn onClick={handleCreate} disabled={actionLoading}>
                  {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Crea
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
